<script setup lang="ts">
  import { useGIDataUploadCollectionStore } from '@/stores/giDataUploadCollection'
  import { computed } from 'vue'
  import GenericDialog from '@/components/dialog/GenericDialog.vue'
  import GICollectionNameField from '@/components/form/GICollectionNameField.vue'
  import GICollectionColorField from '@/components/form/GICollectionColorField.vue'
  import { OpengroundToSpeckleForm } from '@haskoning/vue-speckle'

  const store = useGIDataUploadCollectionStore()

  const title = computed(() => {
    return store.currentItem['id']
      ? `Update ${store.currentItem['name']}`
      : 'Upload GI data files to create a new collection'
  })

  const projectStore = useProjectStore()
  const giPointsStore = useGIPointsStore()

  const { items: allGIPoints } = storeToRefs(giPointsStore)
  const { currentItem: currentProject } = storeToRefs(projectStore)
  const { currentItem: currentCollection } = storeToRefs(store)
  const files = ref([])
  const uploading = ref(false)
  const name = ref('')
  const collectionColor = ref('#000000')

  const errorFiles = ref([])

  const fileRules = (v) => v.length <= 500 || 'You can only upload a maximum of 500 files.'
  const uploadFile = async () => {
    uploading.value = true
    const formData = new FormData()
    files.value.forEach((file) => {
      formData.append('source_files', file)
    })
    formData.append('project', currentProject?.value?.id.toString())
    formData.append('name', name.value)
    if (collectionColor.value) {
      formData.append('collection_color', collectionColor.value)
    }
    try {
      const response = await store.postOrUpdate(formData)
      const hasErrorFiles = response?.error_files?.length > 0
      if (response.gipoint_set) {
        const giPoints = response.gipoint_set.features
        if (currentCollection?.value?.id) {
          const filteredPoints = allGIPoints.value.filter(
            (point) => !(point.properties.gi_data_upload_collection === currentCollection.value.id)
          )
          giPointsStore.setItems(filteredPoints)
        }
        giPointsStore.addItems(giPoints)
        !hasErrorFiles && store.toggleUploadDialog()
      }
      if (hasErrorFiles) {
        errorFiles.value = response.error_files
      }
    } catch (error) {
      console.log(error)
    } finally {
      uploading.value = false
      files.value = []
    }
  }

  onMounted(() => {
    name.value = currentCollection?.value?.name || ''
    collectionColor.value = currentCollection?.value?.collection_color
  })
</script>

<template>
  <GenericDialog
    :model-value="store.showUploadDialog"
    :max-width="600"
    @update:model-value="store.toggleUploadDialog"
  >
    <template #title>{{ title }}</template>
    <template #default>
      <GICollectionNameField v-model="name" />
      <v-file-input
        :multiple="true"
        v-model="files"
        prepend-icon="mdi-folder-zip"
        color="primary"
        class="mb-2"
        :label="currentCollection['id'] ? 'Add GI Data Files' : 'Upload GI data files'"
        accept=".gef, .xml"
        :counter="true"
        hint="Only .gef and .xml files are allowed. Maximum 500 files."
        :persistent-hint="true"
        :rules="[fileRules]"
      >
        <template v-slot:selection="{ fileNames }">
          <template v-for="(fileName, index) in fileNames" :key="fileName">
            <v-chip v-if="index < 5" color="primary" :label="true" size="small" class="me-2">
              {{ fileName }}
            </v-chip>
            <span v-else-if="index === 5" class="text-overline text-grey-darken-3 mx-2">
              +{{ files.length - 5 }} File(s)
            </span>
          </template>
        </template>
      </v-file-input>
      <GICollectionColorField v-model="collectionColor" />
      <v-alert v-if="errorFiles.length > 0" type="warning" variant="flat" prominent :icon="false">
        These files are skipped when creating a new collection due to errors:
        <ol class="ml-6">
          <li v-for="(item, index) in errorFiles" :key="index">
            {{ item }}
          </li>
        </ol>
        Please check the files and try again.
      </v-alert>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="store.toggleUploadDialog">Cancel</v-btn>
      <v-btn
        :disabled="
          name.length <= 0 || files.length > 500 || (!currentCollection['id'] && files.length <= 0)
        "
        color="primary"
        :loading="uploading"
        @click="uploadFile"
      >
        Upload
      </v-btn>
    </template>
  </GenericDialog>
</template>
